import { gql } from '@apollo/client';
import client from 'apollo/client';
import { getLegalFilters } from 'common/filters/requestFilters';
import { toJsonArg } from 'common/utils/graphqlJson';

/**
 * Create legal method
 * @param  {Object} payload
 * @return {Promise}
 */
const create = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createLegal($input: CreateLegalInput!) {
        createLegal(input: $input) {
          id
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Find legal method
 * @param  {string} id
 * @return {Promise}
 */
const find = (id) =>
  client.query({
    query: gql`
      query legal($id: ID!) {
        legal(id: $id) {
          id
          body
          created_at
          language
          role
          title
        }
      }
    `,
    variables: { id },
    fetchPolicy: 'network-only'
  });

/**
 * Force delete legals method
 * @param  {string} ids
 * @return {Promise}
 */
const forceDelete = (ids) =>
  client.mutate({
    mutation: gql`
      mutation forceDeleteLegals($id: [ID!]!) {
        forceDeleteLegals(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids }
  });

/**
 * Get latest legal by role method
 * @param {Object} payload
 * @return {Promise}
 */
const getLatestByRole = (payload) => {
  const { language, role } = payload;

  return client.query({
    query: gql`
      query latestLegalByRole($language: String!, $role: UserRole!) {
        latestLegalByRole(language: $language, role: $role) {
          id
          body
          title
        }
      }
    `,
    variables: { language, role },
    fetchPolicy: 'network-only'
  });
};

/**
 * List legals method
 * @param {Object} payload
 * @returns {Promise}
 */
const list = (payload) => {
  const { fields, filters, pagination } = payload;
  const legalFilters = getLegalFilters(filters);

  return client.query({
    query: gql`
      query legals {
        legals(
          first: ${pagination.first}
          page: ${pagination.page}
          ${legalFilters.language}
          ${legalFilters.role}
          orderBy: {
            column: CREATED_AT,
            order: DESC
          }
        ) {
          paginatorInfo {
            currentPage
            hasMorePages
            total
          }
          data {
            ${fields}
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * Update legal method
 * @param  {Object} payload
 * @return {Promise}
 */
const update = (payload) =>
  client.mutate({
    mutation: gql`
      mutation updateLegal($id: ID!, $input: UpdateLegalInput!) {
        updateLegal(id: $id, input: $input) {
          id
        }
      }
    `,
    variables: {
      id: payload.id,
      input: payload.input
    }
  });

/**
 * Get the authenticated user's acceptance for a legal document.
 * @param {string} legalId
 * @return {Promise}
 */
const getMyAcceptance = (legalId) =>
  client.query({
    query: gql`
      query myLegalAcceptance($legalId: ID!) {
        myLegalAcceptance(legal_id: $legalId) {
          id
          legal_id
          values
          accepted_at
        }
      }
    `,
    variables: { legalId },
    fetchPolicy: 'network-only'
  });

/**
 * Admin: estado de aceptación de un documento por cada usuario de su rol.
 * @param {string} legalId
 * @return {Promise}
 */
const getAcceptances = (legalId) =>
  client.query({
    query: gql`
      query legalAcceptances($legalId: ID!) {
        legalAcceptances(legal_id: $legalId) {
          accepted
          accepted_at
          user {
            id
            full_name
            photo
          }
        }
      }
    `,
    variables: { legalId },
    fetchPolicy: 'network-only'
  });

/**
 * Accept a legal document with the filled-in field values.
 * @param {Object} payload { legalId, values }
 * @return {Promise}
 */
const accept = (payload) =>
  client.mutate({
    mutation: gql`
      mutation acceptLegal($legalId: ID!, $values: JSON!) {
        acceptLegal(legal_id: $legalId, values: $values) {
          id
          legal_id
          values
          accepted_at
        }
      }
    `,
    variables: { legalId: payload.legalId, values: toJsonArg(payload.values) }
  });

export default {
  accept,
  create,
  find,
  forceDelete,
  getAcceptances,
  getLatestByRole,
  getMyAcceptance,
  list,
  update
};
